/**
 * lib/hodAnalysis.ts
 * ------------------------------------------------------------------
 * HOD "Teacher-to-Teacher" analysis — the department grade-distribution
 * document (one workbook/section per subject). For each form level it
 * lists a row per teacher, MERGING that teacher's same-form classes into
 * one teaching set (5K+5L+5M+5N -> "5KLMN"), with the count of pupils in
 * each grade band (A*..U), the Total, Credit Pass % (A*-C) and Pass %
 * (A*-E), plus a per-form Total row.
 *
 * Grades come from the SAME phase-aware engine the reports and Teacher
 * Analysis use (deriveMark + the scheme), so the numbers reconcile with
 * the rest of the app. Pure — no Firestore / React.
 * ------------------------------------------------------------------ */
import {
  deriveMark, schemeForSubject, gradeFor, isBlank, getFormLevel, GRADE_ORDER_ALL,
} from '@/lib/grading';
import type { Student, Subject, Teacher, MarksDocument, MaybeMark, SubjectMarkEntry } from '@/types';

export const HOD_GRADES = GRADE_ORDER_ALL; // A*,A,B,C,D,E,F,G,U
export const CREDIT_GRADES = ['A*', 'A', 'B', 'C'];
export const PASS_GRADES = ['A*', 'A', 'B', 'C', 'D', 'E'];

export interface HodRow {
  teacherName: string;
  classLabel: string;
  gradeCounts: Record<string, number>;
  total: number;
}
export interface HodFormBlock {
  formKey: string;   // '1'..'5' | '6'
  formLabel: string; // 'Form 1' … 'Form 6'
  rows: HodRow[];
}
export interface HodSubjectBlock {
  subjectId: string;
  subjectName: string;
  forms: HodFormBlock[];
}

/* Which form BLOCK a class name belongs to. A-level (AS/A2) -> '6'. */
function blockKeyFor(className: string): string | null {
  const lvl = getFormLevel(className);
  if (lvl === 'AS' || lvl === 'A2') return '6';
  return lvl;
}
const FORM_ORDER = ['1', '2', '3', '4', '5', '6'];
const formLabel = (k: string) => (k === '6' ? 'Form 6' : `Form ${k}`);

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

/* A-level session token, e.g. "AS Jan C" -> "ASJan", "A2 June" -> "A2June". */
function alSession(nm: string): string {
  const m = /^(AS|A2)\s*([A-Za-z]+)/i.exec(nm.trim());
  return m ? `${m[1].toUpperCase()}${cap(m[2])}` : nm.replace(/\s+/g, '');
}
/* Numeric class -> { num, stream }. Handles both "Form 4 K" and "4K". */
function numParts(nm: string): { num: string; stream: string } | null {
  const m = /^(?:Form\s*)?(\d+)\s*([A-Za-z]+)?/i.exec(nm.trim());
  return m ? { num: m[1], stream: (m[2] || '').toUpperCase() } : null;
}

const teacherIdFor = (e: SubjectMarkEntry | undefined, stu: Student, subjId: string) =>
  (e?.teacherId ?? stu.subjectTeachers?.[subjId]) || '__unassigned__';

interface Rec { classId: string; className: string; blockKey: string; form: string; session: string; teacherId: string; e: SubjectMarkEntry; }
interface Grp { blockKey: string; teacherId: string; label: string; entries: SubjectMarkEntry[]; }

/** A class is a "whole class" for a subject (analysed on its own, e.g. "1K")
 *  only when ONE teacher takes the whole class together — i.e. that teacher
 *  covers at least this fraction of the class roster. Below it (an option
 *  subject sat by only a few pupils, or a class split across teachers) the
 *  pupils are a SET, pooled into the combined-form group (e.g. "5KLMNP"). */
const WHOLE_CLASS_MIN_COVERAGE = 0.5;

export function buildHodAnalysis(input: {
  term: string;
  deptSubjects: Subject[];
  teachers: Teacher[];
  students: Student[];
  markDocs: MarksDocument[];
  classNameById: Record<string, string>;
}): HodSubjectBlock[] {
  const { term, deptSubjects, teachers, students, markDocs, classNameById } = input;
  const studentById: Record<string, Student> = {};
  students.forEach((s) => { studentById[s.id] = s; });
  // Registration-class roster sizes — how "whole class" vs "set" is judged.
  const rosterByClass: Record<string, number> = {};
  students.forEach((s) => { if (s.currentClass) rosterByClass[s.currentClass] = (rosterByClass[s.currentClass] || 0) + 1; });
  const tName = (id: string) =>
    id === '__unassigned__' ? '⚠ Unassigned'
      : (teachers.find((t) => t.id === id)?.displayName ?? 'Unknown teacher');

  const out: HodSubjectBlock[] = [];

  for (const subj of deptSubjects) {
    const scheme = schemeForSubject(subj);
    const docs = markDocs.filter((d) => d.subjectId === subj.id);
    if (docs.length === 0) continue;

    // Pass 1 — flatten every graded entry, and learn each numeric class's
    // shape: its teachers (+ how many pupils each takes) and its stream.
    const records: Rec[] = [];
    interface ClassInfo { className: string; stream: string; teacherCounts: Record<string, number>; total: number; }
    const classInfo: Record<string, Record<string, ClassInfo>> = {}; // form -> classId -> info
    for (const doc of docs) {
      const className = classNameById[doc.classId] || doc.classId;
      const blockKey = blockKeyFor(className);
      if (!blockKey) continue;
      const isAL = blockKey === '6';
      const form = isAL ? '6' : blockKey;
      const session = isAL ? alSession(className) : '';
      for (const [studentId, e] of Object.entries(doc.entries ?? {})) {
        const stu = studentById[studentId];
        if (!stu) continue;
        const teacherId = teacherIdFor(e, stu, subj.id);
        records.push({ classId: doc.classId, className, blockKey, form, session, teacherId, e });
        if (!isAL) {
          const ci = ((classInfo[form] ??= {})[doc.classId] ??= { className, stream: numParts(className)?.stream || '', teacherCounts: {}, total: 0 });
          ci.teacherCounts[teacherId] = (ci.teacherCounts[teacherId] || 0) + 1;
          ci.total += 1;
        }
      }
    }

    // Classify each numeric class: WHOLE (one teacher takes ~all of the class
    // roster → its own row) or SET (option subset / split → pooled to the form).
    // `wholeTeacher[form][classId]` = the sole teacher when it's a whole class.
    const wholeTeacher: Record<string, Record<string, string>> = {};
    const setStreamsByForm: Record<string, Set<string>> = {}; // streams the form's SET pool is drawn from
    for (const [form, classes] of Object.entries(classInfo)) {
      for (const [classId, ci] of Object.entries(classes)) {
        const teacherIds = Object.keys(ci.teacherCounts);
        const roster = rosterByClass[classId] || ci.total;
        const sole = teacherIds.length === 1;
        const coverage = roster > 0 ? ci.total / roster : 1;
        if (sole && coverage >= WHOLE_CLASS_MIN_COVERAGE) {
          (wholeTeacher[form] ??= {})[classId] = teacherIds[0];
        } else if (ci.stream) {
          (setStreamsByForm[form] ??= new Set<string>()).add(ci.stream);
        }
      }
    }
    const setLabel = (form: string) => `${form}${[...(setStreamsByForm[form] || new Set<string>())].sort().join('')}`;
    const singleClassLabel = (className: string) => {
      const p = numParts(className);
      return p ? `${p.num}${p.stream}` : className;
    };

    // Pass 2 — assign each entry to its display group.
    const groups = new Map<string, Grp>();
    for (const rec of records) {
      let key: string; let label: string;
      if (rec.blockKey === '6') { key = `6|${rec.session}|${rec.teacherId}`; label = rec.session; }
      else if (wholeTeacher[rec.form]?.[rec.classId]) { key = `${rec.form}|WC|${rec.classId}`; label = singleClassLabel(rec.className); }
      else { key = `${rec.form}|SET|${rec.teacherId}`; label = setLabel(rec.form); }
      let g = groups.get(key);
      if (!g) { g = { blockKey: rec.blockKey, teacherId: rec.teacherId, label, entries: [] }; groups.set(key, g); }
      g.entries.push(rec.e);
    }

    // Reduce each group to phase-aware grade counts (same rule as Teacher Analysis).
    const byBlock = new Map<string, HodRow[]>();
    for (const g of groups.values()) {
      const examPhase = g.entries.some((e) => !isBlank(e.exam as MaybeMark));
      const gradeCounts: Record<string, number> = Object.fromEntries(HOD_GRADES.map((gr) => [gr, 0]));
      let total = 0;
      for (const e of g.entries) {
        const d = deriveMark(term, e.ca, e.exam, scheme);
        const mark: MaybeMark | null = examPhase ? d.gradeSource : d.termMark;
        const gr = examPhase ? d.reportGrade : gradeFor(d.termMark, scheme);
        if (mark === null || mark === undefined || mark === 'ABS') continue;
        if (!Number.isFinite(Number(mark))) continue;
        if (gr && gr in gradeCounts) { gradeCounts[gr] += 1; total += 1; }
      }
      if (total === 0) continue;
      (byBlock.get(g.blockKey) ?? byBlock.set(g.blockKey, []).get(g.blockKey)!).push({
        teacherName: tName(g.teacherId), classLabel: g.label, gradeCounts, total,
      });
    }

    const forms: HodFormBlock[] = FORM_ORDER
      .filter((k) => byBlock.has(k))
      .map((k) => ({
        formKey: k,
        formLabel: formLabel(k),
        rows: (byBlock.get(k) as HodRow[]).sort(
          (a, b) => a.classLabel.localeCompare(b.classLabel, undefined, { numeric: true }) || a.teacherName.localeCompare(b.teacherName),
        ),
      }));

    if (forms.length) out.push({ subjectId: subj.id, subjectName: subj.name, forms });
  }

  out.sort((a, b) => a.subjectName.localeCompare(b.subjectName));
  return out;
}

/** Sum of grade counts across A*-C (credit) or A*-E (pass). */
export function creditCount(gc: Record<string, number>): number {
  return CREDIT_GRADES.reduce((s, g) => s + (gc[g] || 0), 0);
}
export function passCount(gc: Record<string, number>): number {
  return PASS_GRADES.reduce((s, g) => s + (gc[g] || 0), 0);
}
